import { ListResponse, useFetch } from "../infrastructure/useFetch";
import { Config } from "../config";
import { RightsFiltersParams } from "../pages/rights/RightsFilters";
import { PartialNullable } from "../types";
import { useCallback } from "react";
import { RightsIntersectionsFiltersParams } from "../pages/rights-intersections/RightsIntersectionsFilters";

export type TrackReleaseRight = {
  release_id: number;
  display_title: string;
  display_artist_name: string;
  authors_names?: string[];
  code_isrc: string;
  external_id?: string;
  share?: number;
  issue_date?: string;
  expire_date?: string;
  work_contractor?: string;
};

export type CreateRightsRequest = {
  share: number | string,
  external_id: string,
  country_list: Array<any>,
  issue_date: string,
  expire_date: string,
  contractor_id: number,
  release_id_list: Array<number>
  authors_names: Array<string>;
}

export type RevokeRightsRequest = {
  expire_date: string,
  rights_items: Array<{ release_id: number, work_contractor: string }>
}

export type RightsIntersectionRecord = {
  title: string | null;
  display_artist_name: string | null;

  release_id: number | null;
  external_id: string | null;

  contractor_id: number | null;
  contractor_title: string | null;

  imprt_id: number | null;
  share: string | null;
  issue_date: string | null;
  expire_date: string | null;
  country_code: string | null;
}

export type RightIssue = {
  release_id: number;
  title: string;
  artist_title: string;
  code_isrc: string;
  external_id: string;
  share: number;
  common_share: number;
  contractors_list: Array<string>;
  territory_shares: Array<number>;
}


export type RightsIntersectionRecordHard = Record<string | number, string>

export const useRightsApi = () => {
  const client = useFetch();

  const getRightsIntersections = useCallback(async (params: RightsIntersectionsFiltersParams) => {
    const result = await client.get<ListResponse<RightsIntersectionRecord> | RightsIntersectionRecordHard>(
      Config.BASE_API_URL + `/rights/history/`,
      {
        params,
        withCredentials: false
      }
    );

    return result.data;
  }, []);

  const postDownloadReportIntersections = async (params: RightsIntersectionsFiltersParams) => {
    const formData = new FormData();
    formData.append("file", params.file);
    if (params.date_to != null && params.date_to != "") {
      formData.append("date_to", params.date_to);
    }
    if (params.date_from != null && params.date_from != "") {
      formData.append("date_from", params.date_from);
    }

    const result = await client.putAsPutFile(
      Config.BASE_API_URL + `/start_cross_works_report/`,
      formData,
      {
        withCredentials: false
      }
    );

    return result.data;
  };


  const getGeneratingReportTaskStatus = async (taskId: string) => {
    const result = await client.get<any>(Config.BASE_API_URL + `/check_report_status/${taskId}/`, {
      withCredentials: false
    });
    return result.data;
  };

  const downloadGeneratedReport = async (taskId: string) => {
    const result = await client.get<any>(Config.BASE_API_URL + `/download_report/${taskId}/`, {
      withCredentials: false,
      responseType: "blob"
    });

    console.log(result.headers);
    return { data: result.data, disposition: result.headers["content-disposition"] };
  };

  const getRights = async (params: RightsFiltersParams, url?: string) => {
    const result = await client.post<ListResponse<TrackReleaseRight>>(
      url ?? Config.BASE_API_URL + `/rights/`,
      params,
      {
        withCredentials: false
      }
    );
    return result.data;
  };

  const getRightsIssues = async (params: RightsFiltersParams, url?: string) => {
    const result = await client.get<ListResponse<RightIssue>>(
      url ?? Config.BASE_API_URL + `/rights/conflicts/`,
      {
        params,
        withCredentials: false
      }
    );
    return result.data;
  };

  const postShareUpdate = async (params: { share: number, territory_shares: number[] }) => {
    const result = await client.post(
      Config.BASE_API_URL + `/rights/share_update/`,
      params,
      {
        withCredentials: false
      }
    );

    return result.data;
  };

  const createRights = async (params: PartialNullable<CreateRightsRequest>) => {
    const result = await client.post<ListResponse<TrackReleaseRight>>(
      Config.BASE_API_URL + `/rights/add/`,
      params,
      {
        withCredentials: false
      }
    );

    return result.data;
  };

  const revokeRights = async (params: PartialNullable<RevokeRightsRequest>) => {
    const result = await client.post<ListResponse<TrackReleaseRight>>(
      Config.BASE_API_URL + `/rights/revocation/`,
      params,
      {
        withCredentials: false
      }
    );

    return result.data;
  };

  const putUploadRightsExcelFile = useCallback(async (file: File | Blob) => {
    const formData = new FormData();
    formData.append("file", file);
    const result = await client.putAsPutFile(
      Config.BASE_API_URL + `/rights/upload/`,
      formData,
      {
        withCredentials: false
      }
    );

    return result.data;
  }, []);


  return {
    getRights,
    createRights,
    revokeRights,
    putUploadRightsExcelFile,

    getRightsIntersections,
    postDownloadReportIntersections,
    getGeneratingReportTaskStatus,
    downloadGeneratedReport,

    getRightsIssues,
    postShareUpdate
  };
};
